import update from 'immutability-helper';
import React, { useCallback, useMemo } from 'react';
import { closestCorners } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import moment from 'moment';

export const handleWidgetUpdateSettings = (key, value, settings, widgetId) => {
	const widgetSettings = settings?.user?.widgetSettings;

	let updatedValue = {};

	if (widgetSettings) {
		if (widgetSettings[widgetId]) {
			updatedValue = {
				...widgetSettings,
				[widgetId]: {
					...widgetSettings[widgetId],
					[key]: value,
				},
			};
		} else {
			// settings object exists but not for this widget
			updatedValue = {
				...widgetSettings,
				[widgetId]: { [key]: value },
			};
		}
	} else {
		// no settings object exists
		updatedValue = {
			[widgetId]: { [key]: value },
		};
	}
	return {
		type: 'user',
		key: 'widgetSettings',
		value: updatedValue,
	};
};

export const widgetTimeFormatting = (time, end) => {
	const mTime = time ? moment(time) : null;
	const mEnd = end ? moment(end) : null;
	return moment().isSame(mTime, 'day')
		? mEnd
			? `Today at ${mEnd?.format('h:mmA')}`
			: 'Today'
		: moment().add(1, 'day').isSame(mTime, 'day')
		? 'Tomorrow'
		: mTime.format('dddd, MMM Do');
};

export function areArraysEqual(a, b) {
	return (
		Array.isArray(a) &&
		Array.isArray(b) &&
		a.length === b.length &&
		a.every((val, index) => val === b[index])
	);
}

function is(x, y) {
	if (x === y) {
		return x !== 0 || y !== 0 || 1 / x === 1 / y;
	} else {
		// eslint-disable-next-line
		return x !== x && y !== y;
	}
}

export function shallowEqual(objA, objB, customCompare = () => false) {
	if (is(objA, objB)) {
		return true;
	}

	if (
		typeof objA !== 'object' ||
		objA === null ||
		typeof objB !== 'object' ||
		objB === null
	) {
		return false;
	}

	const keysA = Object.keys(objA);
	const keysB = Object.keys(objB);

	if (keysA.length !== keysB.length) {
		return false;
	}

	// Test for A's keys different from B.
	for (let i = 0; i < keysA.length; i++) {
		if (!Object.prototype.hasOwnProperty.call(objB, keysA[i])) {
			return false;
		}
		if (
			!is(objA[keysA[i]], objB[keysA[i]]) &&
			!customCompare(keysA[i], objA[keysA[i]], objB[keysA[i]])
		) {
			return false;
		}
	}

	return true;
}

export function areEqualWithIndexPath(objA, objB) {
	return shallowEqual(objA, objB, (k, a, b) => {
		if (k === 'indexPath') {
			return areArraysEqual(a, b);
		}

		return false;
	});
}

export function areEqualWithCtx(objA, objB) {
	return shallowEqual(objA, objB, (k, a, b) => {
		if (k === 'ctx') {
			return areEqualWithIndexPath(a, b);
		}

		return false;
	});
}

export function dragToEmptyLane(source, destination) {
	const [sourceLaneIndex, sourceItemIndex] = source.indexPath;
	const [destinationLaneIndex] = destination.indexPath;

	// Same list
	if (sourceLaneIndex === destinationLaneIndex) {
		return;
	}

	return (lanes) => {
		return update(lanes, {
			[sourceLaneIndex]: {
				children: {
					$splice: [[sourceItemIndex, 1]],
				},
			},
			[destinationLaneIndex]: {
				children: {
					$unshift: [lanes[sourceLaneIndex].children[sourceItemIndex]],
				},
			},
		});
	};
}

export function dragToPopulatedLane(lanes, source, destination) {
	const [sourceListIndex, sourceItemIndex] = source.indexPath;
	const [destinationListIndex, destinationItemIndex] = destination.indexPath;

	const destinationList = lanes[destinationListIndex];
	const sourceList = lanes[sourceListIndex];

	if (!sourceList || !destinationList || sourceList === destinationList) {
		return;
	}

	if (sourceList !== destinationList) {
		return (lanes) => {
			return update(lanes, {
				[sourceListIndex]: {
					children: {
						$splice: [[sourceItemIndex, 1]],
					},
				},
				[destinationListIndex]: {
					children: {
						$splice: [[destinationItemIndex, 0, source.data]],
					},
				},
			});
		};
	}
}

export function useDragHandlers({
	laneState,
	clonedLaneState,
	activeLaneState,
	activeItemState,
}) {
	const [lanes, setLanes] = laneState;
	const [clonedLanes, setClonedLanes] = clonedLaneState;
	const [activeLane, setActiveLane] = activeLaneState;
	const [activeItem, setActiveItem] = activeItemState;

	const onBulkMove = React.useCallback((itemTypes, lanes) => {
		setLanes((lanes) => {
			let toPush = [];
			let updatedLanes = lanes;

			itemTypes
				.map((type) => lanes[1].children.map((item) => item?.id).indexOf(type))
				.filter((index) => index !== -1)
				.map((index) => toPush.push(lanes[1].children[index]));

			updatedLanes = update(lanes, {
				[0]: {
					children: {
						$push: toPush,
					},
				},
			});

			itemTypes.map((type) => {
				const index = updatedLanes[1].children
					.map((item) => item?.id)
					.indexOf(type);

				updatedLanes = update(updatedLanes, {
					[1]: {
						children: {
							$splice: [[index, 1]],
						},
					},
				});
			});
			return updatedLanes;
		});
	});

	const onForceMove = React.useCallback((itemId, laneId, e) => {
		const active = e?.active?.data?.current || itemId;
		const over = e?.over?.data?.current || laneId;

		const destination = over;
		const [sourceListIndex, sourceIndex] = active.indexPath;
		const [destinationListIndex] = destination.indexPath;

		// Same list
		if (sourceListIndex === destinationListIndex) {
			return;
		}

		return setLanes((lanes) => {
			return update(lanes, {
				[sourceListIndex]: {
					children: {
						$splice: [[sourceIndex, 1]],
					},
				},
				[destinationListIndex]: {
					children: {
						$push: [lanes[sourceListIndex].children[sourceIndex]],
					},
				},
			});
		});
	});

	const onDragStart = React.useCallback(
		(e) => {
			const active = e.active.data.current;

			if (active?.data.type === 'lane') {
				const laneData = activeItemState;
				setActiveLane(laneData);
			} else if (active?.data.type === 'item') {
				const itemData = active;
				setActiveItem(itemData);
				setClonedLanes(lanes);
			}
		},
		[
			lanes,
			// These don't change, but tslint complains
			setActiveLane,
			setActiveItem,
			setClonedLanes,
		]
	);

	const onDragCancel = React.useCallback(() => {
		if (activeLane) setActiveLane(null);
		if (activeItem) setActiveItem(null);
		if (clonedLanes) {
			setLanes(clonedLanes);
			setClonedLanes(null);
		}
	}, [
		clonedLanes,
		activeLane,
		activeItem,
		// These don't change, but tslint complains
		setActiveLane,
		setActiveItem,
		setLanes,
		setClonedLanes,
	]);

	const onDragOver = React.useCallback(
		(e) => {
			const active = e.active.data.current;
			const over = e.over?.data.current;

			if (active.data.type !== 'item') {
				return;
			}

			if (!over) {
				if (clonedLanes) {
					setLanes(clonedLanes);
				}

				return;
			}

			const source = active;

			if (over.data.type === 'lane') {
				const destination = over;
				const mutation = dragToEmptyLane(source, destination);

				if (mutation) {
					return setLanes(mutation);
				}
			}

			const destination = over;
			const mutation = dragToPopulatedLane(lanes, source, destination);

			if (mutation) {
				return setLanes(mutation);
			}
		},
		[
			lanes,
			clonedLanes,
			// These don't change, but tslint complains
			setLanes,
		]
	);

	const onDragEnd = React.useCallback(
		(e) => {
			const active = e.active.data.current;
			const over = e.over?.data.current;

			if (!over) {
				if (clonedLanes) {
					setLanes(clonedLanes);
					setClonedLanes(null);
				}

				if (activeLane) setActiveLane(null);
				if (activeItem) setActiveItem(null);

				return;
			}

			if (over.data.type === 'lane' && active.data.type === 'lane') {
				// Sorting lanes
				const destination = over;
				const [sourceListIndex] = active.indexPath;
				const [destinationListIndex] = destination.indexPath;

				if (sourceListIndex !== destinationListIndex) {
					setLanes((lanes) =>
						arrayMove(lanes, sourceListIndex, destinationListIndex)
					);
				}
			} else if (over.data.type === 'lane' && active.data.type === 'item') {
				// Adding item to lane
				const destination = over;
				const [sourceListIndex, sourceIndex] = active.indexPath;
				const [destinationListIndex] = destination.indexPath;

				// Same list
				if (sourceListIndex === destinationListIndex) {
					return;
				}

				return setLanes((lanes) => {
					return update(lanes, {
						[sourceListIndex]: {
							children: {
								$splice: [[sourceIndex, 1]],
							},
						},
						[destinationListIndex]: {
							children: {
								$unshift: [lanes[sourceListIndex].children[sourceIndex]],
							},
						},
					});
				});
			} else if (over.data.type === 'item' && active.data.type === 'item') {
				// Sorting items
				const itemData = over;
				const [sourceListIndex, sourceItemIndex] = active.indexPath;
				const [destinationListIndex, destinationItemIndex] = itemData.indexPath;

				if (
					sourceListIndex === destinationListIndex &&
					sourceItemIndex !== destinationItemIndex
				) {
					setLanes((lanes) => {
						return update(lanes, {
							[destinationListIndex]: {
								children: {
									$set: arrayMove(
										lanes[destinationListIndex].children,
										sourceItemIndex,
										destinationItemIndex
									),
								},
							},
						});
					});
				}
			}

			if (activeLane) setActiveLane(null);
			if (activeItem) setActiveItem(null);
			if (clonedLanes) setClonedLanes(null);
		},
		[
			activeItem,
			activeLane,
			clonedLanes,

			// These don't change, but tslint complains
			setLanes,
			setActiveLane,
			setActiveItem,
			setClonedLanes,
		]
	);

	return {
		onDragEnd,
		onDragOver,
		onDragStart,
		onDragCancel,
		onForceMove,
		onBulkMove,
	};
}

export function useCustomCollisionDetection(lanes, activeLane) {
	const laneIds = useMemo(
		() =>
			lanes.reduce((mapped, lane) => {
				mapped[lane.id] = true;
				return mapped;
			}, {}),
		[lanes]
	);

	return useCallback(
		(entries, target) => {
			if (activeLane) {
				// Lanes can only collide with lanes
				return closestCorners(
					entries.filter((entry) => {
						return !!laneIds[entry[0]];
					}),
					target
				);
			}

			// Items can only collide with items
			return closestCorners(
				entries.filter((entry) => {
					return !laneIds[entry[0]];
				}),
				target
			);
		},
		[laneIds, activeLane]
	);
}
